"use client";

import { useState, useRef, useEffect } from "react";
import { X, Send, User, Loader2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { useMutation, useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";

const WhatsAppIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} style={style}>
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
);

const PHONE_ICON = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 12, height: 12 }}>
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
);

const SESSION_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

function getOrCreateSession(brandId: string): { sessionId: string; name: string; phone: string } | null {
    const key = `visitor_chat_${brandId}`;
    try {
        const stored = localStorage.getItem(key);
        if (stored) {
            const session = JSON.parse(stored);
            if (Date.now() - session.createdAt < SESSION_EXPIRY_MS) {
                return session;
            }
            // Expired
            localStorage.removeItem(key);
        }
    } catch { /* ignore */ }
    return null;
}

function saveSession(brandId: string, sessionId: string, name: string, phone: string) {
    const key = `visitor_chat_${brandId}`;
    localStorage.setItem(key, JSON.stringify({ sessionId, name, phone, createdAt: Date.now() }));
}

interface TrackingChatDialogProps {
    isOpen: boolean;
    onClose: () => void;
    brandId: string;
    brandName: string;
    orderNumber?: string;
    orderId?: string;
}

export default function TrackingChatDialog({
    isOpen,
    onClose,
    brandId,
    brandName,
    orderNumber,
    orderId,
}: TrackingChatDialogProps) {
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [chatInput, setChatInput] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [sessionStarted, setSessionStarted] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const sendVisitorMessage = useMutation(api.messaging.visitorMessages.sendVisitorMessage);

    // Try to restore session on open
    useEffect(() => {
        if (isOpen && brandId) {
            const existing = getOrCreateSession(brandId);
            if (existing) {
                setSessionId(existing.sessionId);
                setName(existing.name);
                setPhone(existing.phone);
                setSessionStarted(true);
            }
        }
    }, [isOpen, brandId]);

    // Fetch messages for active session
    const messages = useQuery(
        api.messaging.visitorMessages.listVisitorMessages,
        sessionId && brandId ? { brandId: brandId as Id<"brands">, sessionId } : "skip"
    );

    // Scroll to bottom on new messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // Reset state when dialog closes
    useEffect(() => {
        if (!isOpen) {
            setChatInput("");
            setIsSending(false);
        }
    }, [isOpen]);

    const handleStartSession = async () => {
        if (!name.trim() || !phone.trim()) {
            toast.error("Please enter your name and WhatsApp number");
            return;
        }

        const cleanPhone = phone.trim().replace(/[^0-9+]/g, "");
        if (cleanPhone.length < 7 || cleanPhone.length > 15) {
            toast.error("Please enter a valid WhatsApp number (7-15 digits)");
            return;
        }

        const newSessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        setSessionId(newSessionId);
        saveSession(brandId, newSessionId, name.trim(), cleanPhone);
        setSessionStarted(true);

        // Send initial message about the order
        setIsSending(true);
        try {
            const initialMessage = orderNumber
                ? `Hi, I'm ${name.trim()}. I need help with order #${orderNumber}.`
                : `Hi, I'm ${name.trim()}. I have a question for ${brandName}.`;

            await sendVisitorMessage({
                brandId: brandId as Id<"brands">,
                sessionId: newSessionId,
                senderName: name.trim(),
                senderPhone: cleanPhone,
                content: initialMessage,
                orderId: orderId ? orderId as Id<"orders"> : undefined,
                orderNumber,
            });
        } catch (error) {
            console.error("[TrackingChat] Failed to send:", error);
            toast.error("Failed to send message. Please try again.");
        }
        setIsSending(false);
    };

    const handleSendMessage = async () => {
        if (!chatInput.trim() || !sessionId) return;

        const msgText = chatInput.trim();
        setChatInput("");
        setIsSending(true);

        try {
            const session = getOrCreateSession(brandId);
            await sendVisitorMessage({
                brandId: brandId as Id<"brands">,
                sessionId,
                senderName: session?.name || name.trim(),
                senderPhone: session?.phone || phone.trim(),
                content: msgText,
                orderId: orderId ? orderId as Id<"orders"> : undefined,
                orderNumber,
            });
        } catch (error) {
            console.error("[TrackingChat] Failed to send:", error);
            toast.error("Failed to send message. Please try again.");
        }
        setIsSending(false);
    };

    const formatTime = (timestamp: number) => {
        return new Date(timestamp).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                style={{
                    position: "fixed",
                    inset: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    zIndex: 50,
                }}
                className="md:bg-transparent md:pointer-events-none"
                onClick={onClose}
            />

            {/* Chat Widget */}
            <div
                style={{
                    position: "fixed",
                    zIndex: 50,
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "#ffffff",
                    borderRadius: "16px",
                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                    overflow: "hidden",
                }}
                className="inset-4 md:inset-auto md:bottom-6 md:right-6 md:w-[400px] md:max-h-[540px] animate-in slide-in-from-bottom-4 duration-300"
                onClick={(e) => e.stopPropagation()}
            >
                {/* WhatsApp-style Header */}
                <div
                    style={{
                        backgroundColor: "#075E54",
                        color: "#ffffff",
                        padding: "14px 16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexShrink: 0,
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <WhatsAppIcon style={{ height: "24px", width: "24px" }} />
                        <div>
                            <h3 style={{ fontWeight: 600, margin: 0, color: "#ffffff", fontSize: "15px" }}>
                                {orderNumber ? "Order Support" : "Chat with Us"}
                            </h3>
                            <p style={{ fontSize: "12px", margin: 0, color: "#bbf7d0" }}>
                                {brandName} • Quick Response
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            padding: "6px",
                            borderRadius: "50%",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            color: "#ffffff",
                        }}
                        onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)")
                        }
                        onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor = "transparent")
                        }
                    >
                        <X style={{ height: "20px", width: "20px" }} />
                    </button>
                </div>

                {/* Chat Body */}
                <div
                    style={{
                        flex: 1,
                        overflowY: "auto",
                        padding: "16px",
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4cfc4' fill-opacity='0.3'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                        backgroundColor: "#E4DDD6",
                    }}
                >
                    {/* Welcome message */}
                    <div style={{ display: "flex", marginBottom: "16px", justifyContent: "flex-start" }}>
                        <div
                            style={{
                                maxWidth: "90%",
                                borderRadius: "8px",
                                padding: "10px 12px",
                                boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                                backgroundColor: "#ffffff",
                                borderTopLeftRadius: 0,
                            }}
                        >
                            <p style={{ fontSize: "13px", color: "#374151", margin: 0, lineHeight: "1.4" }}>
                                {orderNumber
                                    ? `👋 Hi! Need help with order #${orderNumber}? Send us a message and we'll get back to you right away.`
                                    : `👋 Hi! How can we help you today? Send us a message below.`}
                            </p>
                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "4px" }}>
                                <span style={{ fontSize: "10px", color: "#6b7280" }}>
                                    {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Contact form (before session started) */}
                    {!sessionStarted && (
                        <div style={{ display: "flex", marginBottom: "12px", justifyContent: "flex-end" }}>
                            <div
                                style={{
                                    width: "100%",
                                    borderRadius: "8px",
                                    padding: "12px",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                    backgroundColor: "#DCF8C6",
                                    borderTopRightRadius: 0,
                                }}
                            >
                                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                    <div>
                                        <label style={{ fontSize: "11px", fontWeight: 600, color: "#374151", textTransform: "uppercase", letterSpacing: "0.05em", display: "flex", alignItems: "center", gap: "4px", marginBottom: "4px" }}>
                                            <User style={{ width: "12px", height: "12px" }} />
                                            Your Name
                                        </label>
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            placeholder="Enter your full name"
                                            style={{ width: "100%", backgroundColor: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "8px 12px", fontSize: "13px", outline: "none", color: "#111827", boxSizing: "border-box" }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: "11px", fontWeight: 600, color: "#374151", textTransform: "uppercase", letterSpacing: "0.05em", display: "flex", alignItems: "center", gap: "4px", marginBottom: "2px" }}>
                                            <PHONE_ICON />
                                            WhatsApp Number
                                        </label>
                                        <p style={{ fontSize: "10px", color: "#6b7280", margin: "0 0 4px", lineHeight: "1.3" }}>
                                            We&apos;ll reply to you on this number
                                        </p>
                                        <input
                                            type="tel"
                                            inputMode="numeric"
                                            value={phone}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/[^0-9+]/g, "");
                                                setPhone(val);
                                            }}
                                            placeholder="e.g. +2567XXXXXXXX"
                                            style={{ width: "100%", backgroundColor: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "8px 12px", fontSize: "13px", outline: "none", color: "#111827", boxSizing: "border-box" }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Chat messages (after session started) */}
                    {sessionStarted && messages && messages.map((msg: any) => (
                        <div
                            key={msg._id}
                            style={{
                                display: "flex",
                                marginBottom: "8px",
                                justifyContent: msg.direction === "inbound" ? "flex-end" : "flex-start",
                            }}
                        >
                            <div
                                style={{
                                    maxWidth: "85%",
                                    borderRadius: "8px",
                                    padding: "8px 12px",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                    backgroundColor: msg.direction === "inbound" ? "#DCF8C6" : "#ffffff",
                                    borderTopRightRadius: msg.direction === "inbound" ? 0 : "8px",
                                    borderTopLeftRadius: msg.direction === "inbound" ? "8px" : 0,
                                }}
                            >
                                <p style={{ fontSize: "13px", color: "#111827", margin: 0, whiteSpace: "pre-wrap", overflowWrap: "break-word" }}>
                                    {msg.content}
                                </p>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "4px", marginTop: "4px" }}>
                                    <span style={{ fontSize: "10px", color: "#6b7280" }}>
                                        {formatTime(msg.createdAt)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}

                    <div ref={messagesEndRef} />
                </div>

                {/* Footer */}
                <div
                    style={{
                        backgroundColor: "#F0F0F0",
                        padding: "10px 14px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexShrink: 0,
                    }}
                >
                    {!sessionStarted ? (
                        <button
                            onClick={handleStartSession}
                            disabled={isSending || !name.trim() || !phone.trim()}
                            style={{
                                flex: 1,
                                height: "44px",
                                borderRadius: "9999px",
                                border: "none",
                                backgroundColor: "#075E54",
                                cursor: isSending || !name.trim() || !phone.trim() ? "not-allowed" : "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "8px",
                                color: "#ffffff",
                                fontSize: "14px",
                                fontWeight: 600,
                                opacity: isSending || !name.trim() || !phone.trim() ? 0.5 : 1,
                                transition: "opacity 0.2s",
                            }}
                        >
                            {isSending ? (
                                <>
                                    <Loader2 style={{ height: "18px", width: "18px", animation: "spin 1s linear infinite" }} />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <Send style={{ height: "18px", width: "18px" }} />
                                    Start Chat
                                </>
                            )}
                        </button>
                    ) : (
                        <>
                            <input
                                value={chatInput}
                                onChange={(e) => setChatInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSendMessage();
                                    }
                                }}
                                placeholder="Type a message..."
                                style={{
                                    flex: 1,
                                    backgroundColor: "#ffffff",
                                    border: "none",
                                    borderRadius: "9999px",
                                    padding: "10px 16px",
                                    fontSize: "14px",
                                    outline: "none",
                                    color: "#111827",
                                }}
                            />
                            <button
                                onClick={handleSendMessage}
                                disabled={!chatInput.trim() || isSending}
                                style={{
                                    height: "40px",
                                    width: "40px",
                                    borderRadius: "50%",
                                    border: "none",
                                    backgroundColor: "#075E54",
                                    cursor: chatInput.trim() && !isSending ? "pointer" : "not-allowed",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#ffffff",
                                    flexShrink: 0,
                                    opacity: chatInput.trim() && !isSending ? 1 : 0.5,
                                }}
                            >
                                {isSending ? (
                                    <Loader2 style={{ height: "20px", width: "20px", animation: "spin 1s linear infinite" }} />
                                ) : (
                                    <Send style={{ height: "20px", width: "20px" }} />
                                )}
                            </button>
                        </>
                    )}
                </div>
            </div>

            <style jsx global>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </>
    );
}
